<template>
  <v-card
    flat
    class="pa-3 mt-2"
  >
    <!-- Filter Tabs -->
    <v-tabs
      v-model="activeFilter"
      class="mb-4"
    >
      <v-tab>
        Connections
        <v-badge
          v-if="counts.accepted"
          :content="counts.accepted"
          color="primary"
          inline
        ></v-badge>
      </v-tab>
      <v-tab>
        Pending
        <v-badge
          v-if="counts.pending_incoming"
          :content="counts.pending_incoming"
          color="error"
          inline
        ></v-badge>
      </v-tab>
      <v-tab>
        Sent
        <v-badge
          v-if="counts.pending_outgoing"
          :content="counts.pending_outgoing"
          color="info"
          inline
        ></v-badge>
      </v-tab>
    </v-tabs>

    <!-- Loading -->
    <div
      v-if="loading"
      class="text-center py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      ></v-progress-circular>
    </div>

    <!-- Connections List -->
    <template v-else>
      <!-- Accepted Connections -->
      <div v-if="activeFilter === 0">
        <v-row
          v-if="acceptedConnections.length"
          dense
        >
          <v-col
            v-for="conn in acceptedConnections"
            :key="conn.connection_id"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card
              outlined
              class="pa-3"
            >
              <div class="d-flex align-center">
                <v-avatar
                  size="48"
                  class="me-3 rounded-lg cursor-pointer"
                  tile
                  @click="goToProfile(conn.username)"
                >
                  <v-img
                    :src="getAvatarUrl(conn.avatar)"
                    :alt="conn.name"
                  ></v-img>
                </v-avatar>
                <div class="flex-grow-1 overflow-hidden">
                  <div
                    class="font-weight-medium text-truncate cursor-pointer"
                    @click="goToProfile(conn.username)"
                  >
                    {{ conn.name }}
                  </div>
                  <div class="text-caption text--secondary text-truncate">
                    @{{ conn.username }}
                  </div>
                </div>
                <v-menu
                  bottom
                  left
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon size="20">
                        {{ mdiDotsVertical }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="goToProfile(conn.username)">
                      <v-list-item-icon>
                        <v-icon size="20">
                          {{ mdiAccountOutline }}
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>View Profile</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="confirmRemove(conn)">
                      <v-list-item-icon>
                        <v-icon
                          size="20"
                          color="error"
                        >
                          {{ mdiAccountRemove }}
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="error--text">
                          Remove Connection
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </v-card>
          </v-col>
        </v-row>
        <div
          v-else
          class="text-center py-8 text--secondary"
        >
          <v-icon
            size="48"
            class="mb-3"
          >
            {{ mdiAccountGroup }}
          </v-icon>
          <div>No connections yet.</div>
          <div class="text-caption">
            Visit other users' profiles to send connection requests.
          </div>
        </div>
      </div>

      <!-- Pending Incoming -->
      <div v-if="activeFilter === 1">
        <v-row
          v-if="incomingRequests.length"
          dense
        >
          <v-col
            v-for="conn in incomingRequests"
            :key="conn.connection_id"
            cols="12"
            sm="6"
          >
            <v-card
              outlined
              class="pa-3"
            >
              <div class="d-flex align-center">
                <v-avatar
                  size="48"
                  class="me-3 rounded-lg cursor-pointer"
                  tile
                  @click="goToProfile(conn.username)"
                >
                  <v-img
                    :src="getAvatarUrl(conn.avatar)"
                    :alt="conn.name"
                  ></v-img>
                </v-avatar>
                <div class="flex-grow-1 overflow-hidden">
                  <div
                    class="font-weight-medium text-truncate cursor-pointer"
                    @click="goToProfile(conn.username)"
                  >
                    {{ conn.name }}
                  </div>
                  <div class="text-caption text--secondary text-truncate">
                    @{{ conn.username }}
                  </div>
                </div>
                <div class="d-flex">
                  <v-btn
                    small
                    color="primary"
                    class="me-2"
                    :loading="actionLoading === conn.connection_id"
                    @click="handleRequest(conn.connection_id, 'accept')"
                  >
                    <v-icon
                      small
                      left
                    >
                      {{ mdiCheck }}
                    </v-icon>
                    Accept
                  </v-btn>
                  <v-btn
                    small
                    outlined
                    :loading="actionLoading === conn.connection_id"
                    @click="handleRequest(conn.connection_id, 'reject')"
                  >
                    <v-icon
                      small
                      left
                    >
                      {{ mdiClose }}
                    </v-icon>
                    Decline
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
        <div
          v-else
          class="text-center py-8 text--secondary"
        >
          No pending connection requests.
        </div>
      </div>

      <!-- Sent Outgoing -->
      <div v-if="activeFilter === 2">
        <v-row
          v-if="outgoingRequests.length"
          dense
        >
          <v-col
            v-for="conn in outgoingRequests"
            :key="conn.connection_id"
            cols="12"
            sm="6"
          >
            <v-card
              outlined
              class="pa-3"
            >
              <div class="d-flex align-center">
                <v-avatar
                  size="48"
                  class="me-3 rounded-lg cursor-pointer"
                  tile
                  @click="goToProfile(conn.username)"
                >
                  <v-img
                    :src="getAvatarUrl(conn.avatar)"
                    :alt="conn.name"
                  ></v-img>
                </v-avatar>
                <div class="flex-grow-1 overflow-hidden">
                  <div
                    class="font-weight-medium text-truncate cursor-pointer"
                    @click="goToProfile(conn.username)"
                  >
                    {{ conn.name }}
                  </div>
                  <div class="text-caption text--secondary text-truncate">
                    @{{ conn.username }}
                  </div>
                </div>
                <v-btn
                  small
                  outlined
                  color="error"
                  :loading="actionLoading === conn.connection_id"
                  @click="cancelRequest(conn.connection_id)"
                >
                  <v-icon
                    small
                    left
                  >
                    {{ mdiClose }}
                  </v-icon>
                  Cancel
                </v-btn>
              </div>
            </v-card>
          </v-col>
        </v-row>
        <div
          v-else
          class="text-center py-8 text--secondary"
        >
          No sent connection requests.
        </div>
      </div>
    </template>

    <!-- Remove Confirmation Dialog -->
    <v-dialog
      v-model="removeDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title>Remove Connection</v-card-title>
        <v-card-text v-if="connectionToRemove">
          Are you sure you want to remove <strong>{{ connectionToRemove.name }}</strong> from your connections?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="removeDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            :loading="actionLoading === (connectionToRemove && connectionToRemove.connection_id)"
            @click="removeConnection()"
          >
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import axios from 'axios'
import {
  mdiDotsVertical,
  mdiAccountOutline,
  mdiAccountRemove,
  mdiAccountGroup,
  mdiCheck,
  mdiClose,
} from '@mdi/js'

export default {
  name: 'AccountSettingsConnections',
  data: () => ({
    mdiDotsVertical,
    mdiAccountOutline,
    mdiAccountRemove,
    mdiAccountGroup,
    mdiCheck,
    mdiClose,

    loading: true,
    activeFilter: 0,
    actionLoading: null,

    acceptedConnections: [],
    incomingRequests: [],
    outgoingRequests: [],

    counts: {
      accepted: 0,
      pending_incoming: 0,
      pending_outgoing: 0,
    },

    removeDialog: false,
    connectionToRemove: null,

    snackbar: {
      show: false,
      message: '',
      color: '',
    },

    endpoint: process.env.VUE_APP_ROOT_API,
    defaultAvatar: require('@/assets/images/avatars/1.png'),
  }),
  mounted() {
    this.loadConnections();
  },
  methods: {
    getAuthHeaders() {
      const token = this.$store.state.jwtToken;
      if (token) {
        return { Authorization: token };
      }

      return {};
    },

    getAvatarUrl(avatar) {
      if (avatar) {
        return `https://self-biography-system.s3.us-east-2.amazonaws.com/${avatar}`;
      }

      return this.defaultAvatar;
    },

    goToProfile(username) {
      this.$router.push({ name: 'public_user', params: { username } });
    },

    async loadConnections() {
      this.loading = true;
      try {
        const [acceptedRes, pendingRes] = await Promise.all([
          axios.get(`${this.endpoint}/getConnections?filter=accepted`, { headers: this.getAuthHeaders() }),
          axios.get(`${this.endpoint}/getConnections?filter=pending`, { headers: this.getAuthHeaders() }),
        ]);

        this.acceptedConnections = acceptedRes.data.connections || [];
        this.counts = acceptedRes.data.counts || this.counts;

        const pending = pendingRes.data.connections || [];
        this.incomingRequests = pending.filter(c => c.direction === 'incoming');
        this.outgoingRequests = pending.filter(c => c.direction === 'outgoing');
      } catch {
        this.showSnackbar('Failed to load connections', 'error');
      }
      this.loading = false;
    },

    async handleRequest(connectionId, action) {
      this.actionLoading = connectionId;
      try {
        await axios.post(
          `${this.endpoint}/handleConnectionRequest`,
          { connectionId, action },
          { headers: this.getAuthHeaders() },
        );
        this.showSnackbar(
          action === 'accept' ? 'Connection accepted!' : 'Request declined',
          action === 'accept' ? 'success' : '',
        );
        await this.loadConnections();
      } catch {
        this.showSnackbar('Failed to process request', 'error');
      }
      this.actionLoading = null;
    },

    async cancelRequest(connectionId) {
      this.actionLoading = connectionId;
      try {
        await axios.post(
          `${this.endpoint}/removeConnection`,
          { connectionId },
          { headers: this.getAuthHeaders() },
        );
        this.showSnackbar('Request cancelled');
        await this.loadConnections();
      } catch {
        this.showSnackbar('Failed to cancel request', 'error');
      }
      this.actionLoading = null;
    },

    confirmRemove(conn) {
      this.connectionToRemove = conn;
      this.removeDialog = true;
    },

    async removeConnection() {
      if (!this.connectionToRemove) return;
      this.actionLoading = this.connectionToRemove.connection_id;
      try {
        await axios.post(
          `${this.endpoint}/removeConnection`,
          { connectionId: this.connectionToRemove.connection_id },
          { headers: this.getAuthHeaders() },
        );
        this.showSnackbar('Connection removed');
        this.removeDialog = false;
        this.connectionToRemove = null;
        await this.loadConnections();
      } catch {
        this.showSnackbar('Failed to remove connection', 'error');
      }
      this.actionLoading = null;
    },

    showSnackbar(message, color = '') {
      this.snackbar = { show: true, message, color };
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
